import axios from 'axios'

import type { IChatMessage } from '@/apiClient/types/chat'
import type { IUploadedFile } from '@/apiClient/types/file'

/**
 * True when fetching a published HTML asset failed because it no longer exists
 * (deleted or unpublished). The backend answers `404` for a missing asset and
 * `410 Gone` for one that was explicitly unpublished; both mean "not available".
 */
export function isPublishedHtmlUnavailableError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  const status = error.response?.status
  return status === 404 || status === 410
}

/** Identifies an HTML fence in a chat message for lazy publish/share resolution. */
export interface HtmlPublishKey {
  chatId: string
  messageId: string
  blockIndex: number
}

/**
 * Reads `data-html-block-index` from a rendered markdown HTML asset mount point
 * or legacy `.code-block-wrapper` (must match `postProcessMarkdownHtml` / preview panel indexing).
 */
export function htmlPublishKeyFromCodeBlockElement(
  wrapper: Element | null,
  chatId: string,
  messageId: string,
): HtmlPublishKey | undefined {
  if (!wrapper || !chatId || !messageId) return undefined
  const idxStr = wrapper.getAttribute('data-html-block-index')
  const blockIndex = idxStr !== null ? Number.parseInt(idxStr, 10) : Number.NaN
  if (Number.isNaN(blockIndex)) return undefined
  return { chatId, messageId, blockIndex }
}

const TRUSTED_PUBLISHED_HTML_PATH_RE =
  /^\/(?:api\/v1\/)?publishing\/html\/[^/]+\/[^/]+\/[^/]+\.html$/i

/**
 * True when `apiPath` looks like `/api/v1/publishing/html/{chat}/{message}/{file}.html`
 * (or the same without the `/api/v1` prefix). Used to prevent open redirects on the share page.
 */
export function isTrustedPublishedHtmlApiPath(apiPath: string): boolean {
  const p = apiPath.trim()
  if (!p.startsWith('/') || p.includes('..')) return false
  return TRUSTED_PUBLISHED_HTML_PATH_RE.test(p)
}

/** Normalizes to `/api/v1/publishing/html/...` for GET URLs and share links. */
export function canonicalPublishedHtmlApiPath(apiPath: string): string {
  const p = apiPath.trim().startsWith('/')
    ? apiPath.trim()
    : `/${apiPath.trim()}`
  if (p.startsWith('/api/v1/publishing/html/')) return p
  if (p.startsWith('/publishing/html/')) return `/api/v1${p}`
  return p
}

/**
 * Parsed segments for `/share/html/:chatId/:messageId/:fileName`.
 * Returns null if `apiPath` is not a trusted published-html API path.
 */
export function publishedHtmlShareRouteParams(
  apiPath: string,
): { chatId: string; messageId: string; fileName: string } | null {
  const canonical = canonicalPublishedHtmlApiPath(apiPath)
  if (!isTrustedPublishedHtmlApiPath(canonical)) return null
  const m = canonical.match(
    /^\/(?:api\/v1\/)?publishing\/html\/([^/]+)\/([^/]+)\/([^/]+\.html)$/i,
  )
  if (!m) return null
  return { chatId: m[1], messageId: m[2], fileName: m[3] }
}

/** Canonical API path from share route params (decoded by vue-router). */
export function canonicalPublishedHtmlApiPathFromShareParams(
  chatId: string,
  messageId: string,
  fileName: string,
): string {
  return `/api/v1/publishing/html/${chatId}/${messageId}/${fileName}`
}

/** In-app URL for the public iframe host (`LayoutShared` + share page). */
export function publishedHtmlSharePagePath(apiPath: string): string {
  const params = publishedHtmlShareRouteParams(apiPath)
  if (params == null) {
    const canonical = canonicalPublishedHtmlApiPath(apiPath)
    return `/share/html?path=${encodeURIComponent(canonical)}`
  }
  const enc = encodeURIComponent
  return `/share/html/${enc(params.chatId)}/${enc(params.messageId)}/${enc(
    params.fileName,
  )}`
}

/** Build an absolute URL to open a published HTML asset (GET). */
export function viewerUrlForPublishedApiPath(apiPath: string): string {
  const rawBase = import.meta.env.VITE_API_ENDPOINT ?? ''
  const siteRoot = rawBase.replace(/\/?api\/v1\/?$/i, '')
  const path = canonicalPublishedHtmlApiPath(apiPath)
  if (!siteRoot) return path
  return `${siteRoot.replace(/\/$/, '')}${path}`
}

/**
 * Relative path for `apiClientVersioned` (base URL already includes `/api/v1`).
 * Accepts `/api/v1/publishing/html/...`, `/publishing/html/...`, or full HTTP(S) URLs.
 */
export function publishedHtmlAxiosPathFromApiPath(apiPath: string): string {
  return normalizePublishableHtmlEndpoint(
    canonicalPublishedHtmlApiPath(apiPath),
  )
}

/**
 * Backend returns paths like `/api/v1/publishing/html/...`; client base URL already
 * includes `/api/v1`, so strip that prefix for axios.
 */
function normalizePublishableHtmlEndpoint(endpoint: string): string {
  const s = endpoint.trim()
  if (!s) return s
  try {
    if (/^https?:\/\//i.test(s)) {
      return normalizePublishableHtmlEndpoint(new URL(s).pathname)
    }
  } catch {
    /* ignore invalid URL */
  }
  return s.replace(/^\/api\/v1(?=\/)/, '')
}

/** Prefer server-provided path for block index; otherwise build `/publishing/html/...`. */
export function resolvePublishPathForHtmlBlock(
  endpoints: string[] | null | undefined,
  blockIndex: number,
  fallback?: HtmlPublishKey,
): string | undefined {
  const raw = endpoints?.[blockIndex]
  if (raw) return normalizePublishableHtmlEndpoint(raw)
  if (fallback?.chatId && fallback.messageId) {
    return `/publishing/html/${fallback.chatId}/${fallback.messageId}/${fallback.blockIndex}`
  }
  return undefined
}

const HTML_BLOCK_FILE_NAME_RE = /^html_block_(\d+)\.html$/i

/** Minimal file shape from chat message `files[]` (published HTML attachments). */
type IPublishHtmlFileHint = {
  name?: string | null
  url?: string | null
}

/** File hint including the backend website-link `metadata` (EVGPT-1532). */
type IPublishHtmlFileMetaHint = IPublishHtmlFileHint & {
  metadata?: {
    website_id?: string | null
    website_is_published?: boolean | null
  } | null
}

/** Linked Website for a published HTML block, from backend `file.metadata`. */
export interface PublishedHtmlWebsiteMeta {
  websiteId: string
  isPublished: boolean
}

/**
 * Website a published HTML block maps to, read from the backend-provided
 * `file.metadata` on its `html_block_{n}.html` attachment (evergpt-api EVGPT-1532).
 *
 * This is the authoritative link: it tells the UI which Website to update on
 * re-publish (so it never tries to re-create a website with an already-taken slug)
 * and whether that Website is currently published. Returns `undefined` when the
 * block has no linked website yet (never published, or published only via the
 * legacy per-block path without a Website).
 */
export function websiteMetaForHtmlBlock(
  files: ReadonlyArray<IPublishHtmlFileMetaHint> | null | undefined,
  blockIndex: number,
): PublishedHtmlWebsiteMeta | undefined {
  for (const file of files ?? []) {
    const nameMatch = file.name?.match(HTML_BLOCK_FILE_NAME_RE)
    if (!nameMatch || Number.parseInt(nameMatch[1], 10) !== blockIndex) continue
    const websiteId = file.metadata?.website_id?.trim()
    if (websiteId) {
      return {
        websiteId,
        isPublished: Boolean(file.metadata?.website_is_published),
      }
    }
  }
  return undefined
}

/**
 * Published HTML often arrives as assistant `files[]`: `html_block_{n}.html` + `url` under `/publishing/html/`.
 */
export function publishedHtmlUrlsFromAttachedFiles(
  files: ReadonlyArray<IPublishHtmlFileHint> | null | undefined,
): Record<number, string> {
  const out: Record<number, string> = {}
  if (!files?.length) return out
  for (const f of files) {
    const url = f.url?.trim()
    if (!url || !url.includes('/publishing/html/')) continue
    const nameMatch = f.name?.match(HTML_BLOCK_FILE_NAME_RE)
    if (!nameMatch) continue
    const idx = Number.parseInt(nameMatch[1], 10)
    if (Number.isNaN(idx)) continue
    out[idx] = url
  }
  return out
}

/**
 * Stable fingerprint of assistant message fields that affect HTML preview / publish
 * (content, `publishable_html_endpoints`, published HTML rows in `files[]`).
 */
export function assistantMessageHtmlPreviewFingerprint(
  message: IChatMessage,
): string {
  const ep = (message.publishable_html_endpoints ?? []).join('\u0001')
  const pubMap = publishedHtmlUrlsFromAttachedFiles(message.files)
  const pubSig = Object.keys(pubMap)
    .map(Number)
    .sort((a, b) => a - b)
    .map((idx) => `${idx}:${pubMap[idx]}`)
    .join('\u0001')
  return `${message.content ?? ''}\u0002${ep}\u0003${pubSig}`
}

/** Resolved file UUID for a published HTML block from assistant `files[]`. */
export function publishedHtmlFileIdForBlock(
  files:
    | ReadonlyArray<IPublishHtmlFileHint & { id?: string | null }>
    | null
    | undefined,
  blockIndex: number,
): string | undefined {
  for (const file of files ?? []) {
    const nameMatch = file.name?.match(HTML_BLOCK_FILE_NAME_RE)
    if (!nameMatch || Number.parseInt(nameMatch[1], 10) !== blockIndex) continue

    const url = file.url?.trim()
    if (url) {
      const fromUrl = url.match(/\/([a-f0-9-]{36})\.html(?:\?|$)/i)?.[1]
      if (fromUrl) return fromUrl
    }

    const id = file.id?.trim()
    if (id) return id
  }

  return undefined
}

/** Resolved URL for a published HTML block from assistant `files[]`, if present. */
export function publishedHtmlApiUrlForBlock(
  files: ReadonlyArray<IPublishHtmlFileHint> | null | undefined,
  blockIndex: number,
): string | undefined {
  const map = publishedHtmlUrlsFromAttachedFiles(files)
  const direct = map[blockIndex]
  if (direct) return direct
  return (map as Record<string, string>)[String(blockIndex)]
}

/** Publish POST path + published asset URL for one HTML fence, from current message data. */
export function resolveHtmlPreviewPublishFromMessage(
  message: IChatMessage | undefined | null,
  publishKey: HtmlPublishKey,
): {
  publishPath: string | undefined
  publishedHtmlApiUrl: string | undefined
} {
  const { chatId, messageId, blockIndex } = publishKey
  const publishPath = resolvePublishPathForHtmlBlock(
    message?.publishable_html_endpoints,
    blockIndex,
    message?.id ? { chatId, messageId, blockIndex } : undefined,
  )
  const publishedHtmlApiUrl = publishedHtmlApiUrlForBlock(
    message?.files,
    blockIndex,
  )
  return { publishPath, publishedHtmlApiUrl }
}

/** Synthetic `files[]` row for a published HTML block — omit from chat file chips. */
export function isPublishedHtmlChatAttachment(
  file: IPublishHtmlFileHint,
): boolean {
  const url = file.url?.trim()
  if (!url || !url.includes('/publishing/html/')) return false
  return HTML_BLOCK_FILE_NAME_RE.test(file.name?.trim() ?? '')
}

/**
 * After a successful publish, mirror API shape: `html_block_{n}.html` + `url` on `message.files`.
 */
export function upsertPublishedHtmlFileAttachment(
  files: IUploadedFile[] | undefined | null,
  blockIndex: number,
  publishedUrl: string,
): IUploadedFile[] {
  const existing = [...(files ?? [])]
  const isBlockRow = (file: IUploadedFile) => {
    const nameMatch = file.name?.match(HTML_BLOCK_FILE_NAME_RE)
    if (!nameMatch) return false
    return Number.parseInt(nameMatch[1], 10) === blockIndex
  }

  // The row is rebuilt from the publish response, which carries no `metadata` — but
  // the per-block publish doesn't touch the block's Website link (EVGPT-1532), so
  // carry it over. Dropping it makes the block read as "not the chat's live page"
  // until the website request lands and re-stamps it, which flashes the
  // "one published webpage per conversation" warning mid-update.
  const previousMetadata = existing.find(isBlockRow)?.metadata
  const filtered = existing.filter((file) => !isBlockRow(file))

  const id =
    publishedUrl.match(/\/([a-f0-9-]{36})\.html(?:\?|$)/i)?.[1] ??
    globalThis.crypto?.randomUUID?.() ??
    `published-html-${blockIndex}-${Date.now()}`

  filtered.push({
    id,
    name: `html_block_${blockIndex}.html`,
    content_type: 'text/html',
    size: 0,
    url: publishedUrl,
    status: 'processed',
    ...(previousMetadata ? { metadata: previousMetadata } : {}),
  })

  return filtered
}

/** Remove published HTML attachment for a block (inverse of `upsertPublishedHtmlFileAttachment`). */
export function removePublishedHtmlFileAttachment(
  files: IUploadedFile[] | undefined | null,
  blockIndex: number,
): IUploadedFile[] {
  return (files ?? []).filter((f) => {
    const m = f.name?.match(HTML_BLOCK_FILE_NAME_RE)
    if (!m) return true
    return Number.parseInt(m[1], 10) !== blockIndex
  })
}

/**
 * Set the linked-website metadata on a block's `html_block_{n}.html` file, keeping
 * the file in place. Mirrors the backend EVGPT-1532 enrichment for optimistic UI:
 * on publish, stamp `{website_id, website_is_published:true}`; on unpublish, keep the
 * file and `website_id` but flip `website_is_published` to `false`. Publish state is
 * then read consistently from `file.metadata` without a chat refetch.
 */
export function setPublishedHtmlFileWebsiteMeta(
  files: IUploadedFile[] | undefined | null,
  blockIndex: number,
  website: { websiteId: string; isPublished: boolean },
): IUploadedFile[] {
  return (files ?? []).map((f) => {
    const m = f.name?.match(HTML_BLOCK_FILE_NAME_RE)
    if (!m || Number.parseInt(m[1], 10) !== blockIndex) return f
    return {
      ...f,
      metadata: {
        ...(f.metadata ?? {}),
        website_id: website.websiteId,
        website_is_published: website.isPublished,
      },
    }
  })
}

/**
 * Clear the linked-website metadata on any `html_block_{n}.html` file whose
 * `metadata.website_id` matches `websiteId` — except an optional `keepBlockIndex`.
 * Used when a website is (re)published from a different block (override): the blocks
 * it previously served are evicted and must stop reading as published (EVGPT-1474).
 * Returns the same array instance when nothing changed, to avoid needless re-renders.
 */
export function clearPublishedHtmlFileWebsiteMetaForWebsite(
  files: IUploadedFile[] | undefined | null,
  websiteId: string,
  keepBlockIndex?: number,
): IUploadedFile[] {
  const list = files ?? []
  let changed = false
  const next = list.map((f) => {
    const m = f.name?.match(HTML_BLOCK_FILE_NAME_RE)
    if (!m || f.metadata?.website_id !== websiteId) return f
    if (
      keepBlockIndex !== undefined &&
      Number.parseInt(m[1], 10) === keepBlockIndex
    ) {
      return f
    }
    changed = true
    return {
      ...f,
      metadata: {
        ...(f.metadata ?? {}),
        website_id: null,
        website_is_published: false,
      },
    }
  })
  return changed ? next : list
}

/**
 * Website slug an old share link maps to, from its `:fileName` route param.
 *
 * Published HTML used to be shared per file at
 * `/share/html/{chatId}/{messageId}/{fileId}.html`. Every published asset is a
 * Website now, served at `/w/{slug}`, and a migrated asset keeps its file id as
 * its slug — so the old link's file name minus `.html` is the new slug. Old links
 * are already out in the wild (emails, chats), hence the redirect.
 *
 * Returns `''` when there is no usable file name, which the route treats as
 * "nothing to redirect to".
 */
export function websiteSlugFromLegacyShareFileName(
  fileName: string | string[] | undefined,
): string {
  const raw = Array.isArray(fileName) ? fileName[0] : fileName
  if (typeof raw !== 'string') return ''
  return raw
    .trim()
    .replace(/\.html$/i, '')
    .trim()
}
